import React from "react";
import { createRoot } from "react-dom/client";
import { QRCodeCanvas } from "qrcode.react";
import { jsPDF } from "jspdf";
import { FusionBladeMark } from "@app/shared/src/components_v2";

/**
 * The printable cab card: a 4×6 inch card on a letter page, ready to cut out and laminate.
 *
 * Letter rather than a 4×6 page because this gets printed on whatever is in the office, and a
 * 4×6 PDF on a letter printer either scales or crops. The card sits inside a dashed cut line at
 * true size, with a note to print at 100%, so the laminated result is actually 4×6.
 *
 * Text is drawn as PDF text (vector, sharp at any zoom); only the QR and the shield are images,
 * rasterised well above print resolution.
 */

const BLADE_BLUE: [number, number, number] = [79, 91, 245];
const INK: [number, number, number] = [11, 13, 18];
const MUTED: [number, number, number] = [90, 99, 112];

/** Mounts a React element off-screen, lets it paint, and hands back the node. */
async function withRenderedElement<T>(element: React.ReactElement, read: (host: HTMLElement) => T): Promise<T> {
    const host = document.createElement("div");
    host.style.position = "fixed";
    host.style.left = "-10000px";
    host.style.top = "0";
    document.body.appendChild(host);
    const root = createRoot(host);
    try {
        root.render(element);
        // Two frames: one for React to commit, one for the canvas effect to draw.
        await new Promise((r) => requestAnimationFrame(() => requestAnimationFrame(r)));
        return read(host);
    } finally {
        root.unmount();
        host.remove();
    }
}

/** The QR as a PNG data URL, rendered large so it stays crisp on paper. */
async function renderQrPng(value: string): Promise<string> {
    return withRenderedElement(
        React.createElement(QRCodeCanvas, { value, size: 720, marginSize: 1, level: "M" }),
        (host) => {
            const canvas = host.querySelector("canvas");
            if (!canvas) throw new Error("QR canvas did not render");
            return canvas.toDataURL("image/png");
        },
    );
}

/**
 * The official FusionBlade artwork, if it has been dropped into the app's public folder.
 *
 * Drawn artwork is a stand-in, never the brand: the moment `public/fusionblade-logo.png` exists it
 * is used instead, at whatever aspect ratio the file has. A missing file simply falls through to
 * the built-in mark, so the card always prints.
 */
async function loadBrandArtwork(): Promise<{ dataUrl: string; aspect: number } | undefined> {
    try {
        const response = await fetch(`${process.env.PUBLIC_URL ?? ""}/fusionblade-logo.png`, { cache: "no-cache" });
        if (!response.ok) return undefined;
        const blob = await response.blob();
        if (!blob.type.startsWith("image/")) return undefined;

        const dataUrl = await new Promise<string>((resolve, reject) => {
            const reader = new FileReader();
            reader.onload = () => resolve(String(reader.result));
            reader.onerror = () => reject(new Error("Could not read the FusionBlade artwork"));
            reader.readAsDataURL(blob);
        });

        const image = await new Promise<HTMLImageElement>((resolve, reject) => {
            const img = new Image();
            img.onload = () => resolve(img);
            img.onerror = () => reject(new Error("Could not decode the FusionBlade artwork"));
            img.src = dataUrl;
        });

        return { dataUrl, aspect: image.naturalWidth / image.naturalHeight };
    } catch {
        return undefined;
    }
}

/** The shield mark as a PNG data URL, rasterised at print resolution. */
async function renderMarkPng(): Promise<string> {
    const svgMarkup = await withRenderedElement(React.createElement(FusionBladeMark, { size: 420 }), (host) => {
        const svg = host.querySelector("svg");
        if (!svg) throw new Error("FusionBlade mark did not render");
        return new XMLSerializer().serializeToString(svg);
    });

    const url = URL.createObjectURL(new Blob([svgMarkup], { type: "image/svg+xml;charset=utf-8" }));
    try {
        const image = await new Promise<HTMLImageElement>((resolve, reject) => {
            const img = new Image();
            img.onload = () => resolve(img);
            img.onerror = () => reject(new Error("Could not rasterise the FusionBlade mark"));
            img.src = url;
        });
        const canvas = document.createElement("canvas");
        canvas.width = 420;
        canvas.height = 475;
        const ctx = canvas.getContext("2d");
        if (!ctx) throw new Error("Canvas is unavailable");
        ctx.drawImage(image, 0, 0, canvas.width, canvas.height);
        return canvas.toDataURL("image/png");
    } finally {
        URL.revokeObjectURL(url);
    }
}

export type CabCardOptions = {
    /**
     * What a driver calls this truck — the facility or TMA name. This is the line someone reads to
     * confirm they are holding the right card, so it takes the prominent slot; the device key is
     * the serial underneath it, useful to support and nobody else.
     */
    title: string;
    deviceKey?: string;
    /** What the QR resolves to — the cab screen for this truck. */
    url: string;
};

export async function downloadCabCardPdf({ title, deviceKey, url }: CabCardOptions): Promise<void> {
    const [qrPng, markPng, brand] = await Promise.all([renderQrPng(url), renderMarkPng(), loadBrandArtwork()]);

    const doc = new jsPDF({ unit: "in", format: "letter", orientation: "portrait", compress: true });
    const pageWidth = 8.5;

    const cardW = 4;
    const cardH = 6;
    const cardX = (pageWidth - cardW) / 2;
    const cardY = 1.1;
    const pad = 0.32;
    const centre = cardX + cardW / 2;

    // Cut line, and the instruction that keeps the printed card at true size.
    doc.setDrawColor(150);
    doc.setLineWidth(0.008);
    doc.setLineDashPattern([0.06, 0.05], 0);
    doc.rect(cardX, cardY, cardW, cardH);
    doc.setLineDashPattern([], 0);

    doc.setFont("helvetica", "normal");
    doc.setFontSize(8);
    doc.setTextColor(150);
    doc.text("Cut along the dashed line and laminate · print at 100% scale (not “fit to page”)", centre, cardY - 0.22, {
        align: "center",
    });

    // ---- Brand lock-up ----
    const brandY = cardY + pad;

    /** Stand-in used only until the official artwork is present. */
    const drawBuiltInLockup = () => {
    const markH = 0.62;
    const markW = markH * (420 / 475);
    // The mark plus the wordmark, measured together so the pair sits centred as one unit.
    doc.setFont("helvetica", "bold");
    doc.setFontSize(21);
    const wordW = doc.getTextWidth("FusionBlade");
    const lockupW = markW + 0.14 + wordW;
    const lockupX = centre - lockupW / 2;

    doc.addImage(markPng, "PNG", lockupX, brandY, markW, markH, undefined, "FAST");

    const textX = lockupX + markW + 0.14;
    doc.setTextColor(...INK);
    doc.text("Fusion", textX, brandY + 0.34);
    doc.setTextColor(...BLADE_BLUE);
    doc.text("Blade", textX + doc.getTextWidth("Fusion"), brandY + 0.34);

    doc.setFont("helvetica", "normal");
    doc.setFontSize(7);
    doc.setTextColor(...MUTED);
    doc.text("B Y   O M N I S I G H T", textX + 0.02, brandY + 0.52);
    };

    if (brand) {
        // Official artwork: one image, sized to the card and centred.
        const maxW = cardW - pad * 2;
        const maxH = 0.78;
        const w = Math.min(maxW, maxH * brand.aspect);
        const h = w / brand.aspect;
        doc.addImage(brand.dataUrl, "PNG", centre - w / 2, brandY, w, h, undefined, "FAST");
    } else {
        drawBuiltInLockup();
    }

    // ---- The instruction the card exists for ----
    let y = cardY + 1.18;
    doc.setDrawColor(220);
    doc.setLineWidth(0.006);
    doc.line(cardX + pad, y, cardX + cardW - pad, y);

    y += 0.34;
    doc.setFont("helvetica", "bold");
    doc.setFontSize(13);
    doc.setTextColor(...INK);
    doc.text(doc.splitTextToSize("This vehicle is equipped with a rear-facing alert system.", cardW - pad * 2), centre, y, {
        align: "center",
        lineHeightFactor: 1.35,
    });

    y += 0.62;
    doc.setFont("helvetica", "normal");
    doc.setFontSize(11);
    doc.setTextColor(...MUTED);
    doc.text(
        doc.splitTextToSize("Scan this QR code to test the lights and horn each time after turning on this vehicle.", cardW - pad * 2),
        centre,
        y,
        { align: "center", lineHeightFactor: 1.4 },
    );

    // ---- QR ----
    const qrSize = 1.9;
    const qrY = y + 0.42;
    doc.addImage(qrPng, "PNG", centre - qrSize / 2, qrY, qrSize, qrSize, undefined, "FAST");

    // ---- Which truck this is ----
    let infoY = qrY + qrSize + 0.34;
    doc.setFont("helvetica", "bold");
    doc.setFontSize(16);
    doc.setTextColor(...INK);
    doc.text(doc.splitTextToSize(title, cardW - pad * 2), centre, infoY, { align: "center", lineHeightFactor: 1.25 });

    if (deviceKey) {
        infoY += 0.2;
        doc.setFont("courier", "normal");
        doc.setFontSize(9);
        doc.setTextColor(...MUTED);
        doc.text(deviceKey, centre, infoY, { align: "center" });
    }

    // ---- Footer ----
    const footY = cardY + cardH - pad;
    doc.setDrawColor(230);
    doc.line(cardX + pad, footY - 0.28, cardX + cardW - pad, footY - 0.28);
    doc.setFont("helvetica", "normal");
    doc.setFontSize(8.5);
    doc.setTextColor(...MUTED);
    doc.text("No login needed · opens this truck only · powered by Omnisight", centre, footY - 0.08, { align: "center" });

    doc.save(`fusionblade-cab-card-${title.replace(/[^\w-]+/g, "-").toLowerCase()}.pdf`);
}
